import type { BillingInformation } from "@/interfaces/billingInformations";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ToastInterface, useToast } from "vue-toastification";
import { MutationParams } from "../interfaces/mutationParams";

interface Props {
  params: Params;
  immediate?: boolean;
}

interface Params extends Omit<Partial<BillingInformation>, "id"> {
  id?: string | number;
  includes?: string[];
}

interface CreateBillingInformation {
  rfc: string;
  legalName: string;
  companyId: any;
  taxSystemId: any;
  addressId: any;
}

interface updateBillingInformation {
  file: File;
  id: string | number;
}

const getBillingInformation = async (params: Params) => {
  const { data } = await axios.get<BillingInformation>(
    `/api/billing-informations/${params.id}`,
    {
      params,
    },
  );
  return data;
};

const createBillingInformation = async (params: CreateBillingInformation) => {
  const { data } = await axios.post<BillingInformation>(
    "/api/billing-informations",
    params,
  );
  return data;
};

export const updateBillingInformation = async (
  params: updateBillingInformation,
  toast: ToastInterface,
) => {
  try {
    const formData = new FormData();
    formData.append("file", params.file);
    formData.append("_method", "put");

    const { data } = await axios.post<BillingInformation>(
      `/api/billing-informations/${params.id}`,
      formData,
    );

    toast.success("La información fiscal se ha actualizado correctamente");

    return data;
  } catch (e: any) {
    toast.error(e.response?.data.message);
  }
};

const useBillingInformation = (props: Props) => {
  const { params, immediate = true } = props;
  const toast = useToast();

  const { data: billingInformation, isLoading } = useQuery<BillingInformation>(
    ["billingInformation", params.id],
    () => getBillingInformation(params),
    {
      enabled: immediate,
      retry: false,
    },
  );

  const createMutation = useMutation(
    (props: MutationParams<CreateBillingInformation>) =>
      createBillingInformation(props.params),
    {
      onSuccess: (_, variables) => {
        const { raiseToast, onSuccessToast } = variables.config || {};
        raiseToast &&
          toast.success(onSuccessToast || "Información de facturación creada");
      },
      onError: (_, variables) => {
        const { raiseToast, onErrorToast } = variables.config || {};
        raiseToast &&
          toast.error(
            onErrorToast || "Error al crear la información de facturación",
          );
      },
    },
  );

  const updateMutation = useMutation(
    (props: MutationParams<updateBillingInformation>) =>
      updateBillingInformation(props.params, toast),
  );

  return {
    billingInformation,
    isLoading,

    createBillingInformationMutation: createMutation,
    createBillingInformation: createMutation.mutate,
    createBillingInformationAsync: createMutation.mutateAsync,

    updateBillingInformationMutation: updateMutation,
    updateBillingInformation: updateMutation.mutate,
    updateBillingInformationAsync: updateMutation.mutateAsync,
    updateBillingInformationLoading: updateMutation.isLoading,
  };
};

export default useBillingInformation;
